import config from "@/config";
import { formatTranslationsVersionLine } from "./translations-version";

const MANIFEST_FETCH_TIMEOUT_MS = 3000;

export type TranslationManifestResourceEntry = {
  path: string;
  hash: string;
};

export type TranslationManifest = {
  locales: string[];
  resources?: Record<string, Record<string, TranslationManifestResourceEntry>>;
};

function getManifestUrl(): string {
  return `${config.translations.cdnBaseUrl}/ui/${formatTranslationsVersionLine()}/manifest.json`;
}

function isTranslationManifest(value: unknown): value is TranslationManifest {
  if (typeof value !== "object" || value === null || !("locales" in value)) {
    return false;
  }

  const locales = value.locales;
  return (
    Array.isArray(locales) &&
    locales.every((locale) => typeof locale === "string")
  );
}

function isManifestResourceEntry(
  value: unknown
): value is TranslationManifestResourceEntry {
  return (
    typeof value === "object" &&
    value !== null &&
    "path" in value &&
    typeof value.path === "string" &&
    "hash" in value &&
    typeof value.hash === "string"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function resourcePathsFromManifest(
  manifest: TranslationManifest
): Record<string, Record<string, string>> | null {
  if (!isRecord(manifest.resources)) {
    return null;
  }

  const resourcePaths: Record<string, Record<string, string>> = {};

  for (const [namespace, locales] of Object.entries(manifest.resources)) {
    if (!isRecord(locales)) {
      continue;
    }

    const localePaths: Record<string, string> = {};
    for (const [locale, entry] of Object.entries(locales)) {
      if (isManifestResourceEntry(entry)) {
        localePaths[locale] = entry.path;
      }
    }

    if (Object.keys(localePaths).length > 0) {
      resourcePaths[namespace] = localePaths;
    }
  }

  return Object.keys(resourcePaths).length > 0 ? resourcePaths : null;
}

export async function fetchTranslationManifest(): Promise<TranslationManifest | null> {
  try {
    const response = await fetch(getManifestUrl(), {
      cache: "no-store",
      signal: AbortSignal.timeout(MANIFEST_FETCH_TIMEOUT_MS),
    });

    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();
    if (!isTranslationManifest(data)) {
      return null;
    }

    return data;
  } catch {
    return null;
  }
}
